import { FC, useEffect } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from 'components/ui/Button';
import { Dropdown } from 'components/forms/Dropdown';
import { InputText } from 'components/forms/InputText';
import { CheckboxComponent } from 'components/forms/Checkbox';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikSwitch } from 'components/forms/formik/FormikSwitch';
import { ActorOption } from '../hooks/useActorOptions';
import { useBankAccountOptions } from '../hooks/useBankAccountOptions';

export interface CoInsurerValues {
  coAssureurPersonneMoraleId: string;
  compteBancaireId: string;
  tauxQP: number | '';
  aperition: boolean;
}

export interface ReInsurerValues {
  reAssureurPersonneMoraleId: string;
  compteBancaireId: string;
  tauxQP: number | '';
  xS: boolean;
  porteeXS: number | '';
  attachementXS: number | '';
  qP: boolean;
}

export const emptyCoInsurer = (): CoInsurerValues => ({
  coAssureurPersonneMoraleId: '',
  compteBancaireId: '',
  tauxQP: '',
  aperition: false,
});

export const emptyReInsurer = (): ReInsurerValues => ({
  reAssureurPersonneMoraleId: '',
  compteBancaireId: '',
  tauxQP: '',
  xS: false,
  porteeXS: '',
  attachementXS: '',
  qP: false,
});

const toDropdownItems = (insurers: ActorOption[]) =>
  insurers.map((insurer) => ({ key: insurer.key, label: insurer.label }));

/** Assureur apériteur : sélection du porteur puis de son compte bancaire. */
export const PrincipalInsurerFields: FC<{ insurers: ActorOption[]; isLoading: boolean }> = ({
  insurers,
  isLoading,
}) => {
  const { values, setFieldValue } = useFormikContext<{
    assureurPersonneMoraleId: string;
    compteBancaireId: string;
  }>();
  const bankAccounts = useBankAccountOptions(values.assureurPersonneMoraleId);

  useEffect(() => {
    if (!values.compteBancaireId && bankAccounts.defaultAccountId) {
      void setFieldValue('compteBancaireId', bankAccounts.defaultAccountId);
    }
  }, [bankAccounts.defaultAccountId, values.compteBancaireId, setFieldValue]);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormikDropdown
          name="assureurPersonneMoraleId"
          label="Compagnie"
          required
          items={toDropdownItems(insurers)}
          placeholder={isLoading ? 'Chargement…' : 'Sélectionner une compagnie'}
        />
        <FormikDropdown
          name="compteBancaireId"
          label="Compte bancaire"
          required
          items={bankAccounts.options}
          disabled={!values.assureurPersonneMoraleId}
          placeholder={
            !values.assureurPersonneMoraleId
              ? "Sélectionner d'abord une compagnie"
              : bankAccounts.options.length
                ? 'Sélectionner un compte'
                : 'Aucun compte disponible'
          }
        />
      </div>
      <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-2">
        <FormikInputNumber name="tauxQP" label="Quote-part (%)" />
        <FormikSwitch name="aperition" label="Apérition" />
      </div>
    </div>
  );
};

export interface QuotePartValues {
  tauxQP: number | '';
  coAssureurs: Array<{ coAssureurPersonneMoraleId: string; tauxQP: number | '' }>;
  reAssureurs: Array<{ reAssureurPersonneMoraleId: string; tauxQP: number | '' }>;
}

export interface QuotePartSplit {
  lead: number;
  co: number;
  re: number;
  total: number;
}

const sumQuoteParts = (rows: Array<{ tauxQP: number | '' }>) =>
  rows.reduce((sum, item) => sum + Number(item.tauxQP || 0), 0);

/**
 * Même règle que le back (`ExerciceGarantieTunnel.IsValidQuotesParts`) : l'apériteur,
 * les co-assureurs ET les ré-assureurs se partagent les 100 %. Seules comptent les lignes
 * avec une compagnie, celles que l'enregistrement envoie (les autres sont écartées).
 * Arrondi au centième : des saisies décimales additionnées en flottant ne doivent pas
 * afficher un reste de 1e-14.
 */
export const splitQuoteParts = (values: QuotePartValues): QuotePartSplit => {
  const lead = Number(values.tauxQP || 0);
  const co = sumQuoteParts(values.coAssureurs.filter((row) => row.coAssureurPersonneMoraleId));
  const re = sumQuoteParts(values.reAssureurs.filter((row) => row.reAssureurPersonneMoraleId));
  const round = (n: number) => Math.round(n * 100) / 100;
  return { lead: round(lead), co: round(co), re: round(re), total: round(lead + co + re) };
};

export const totalQuotePart = (values: QuotePartValues): number => splitQuoteParts(values).total;

/**
 * Répartition du risque, une seule fois pour les trois sections : la règle les traverse
 * toutes, aucune ne peut porter le message seule. Le détail dit quoi corriger, l'ancien
 * écran laissait enregistrer une répartition à 130 % sans le moindre signal.
 */
export const RiskSplitSummary: FC = () => {
  const { values } = useFormikContext<QuotePartValues>();
  const { lead, co, re, total } = splitQuoteParts(values);
  const isValid = total === 100;

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-4 py-2.5 text-sm ${
        isValid ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-700'
      }`}
    >
      <span className="font-semibold">Répartition du risque</span>
      <span className="opacity-80">
        Apériteur {lead} % + Co-assureurs {co} % + Ré-assureurs {re} % =
      </span>
      <span className="font-mono text-base font-semibold">{total} %</span>
      {!isValid && (
        <span>
          ({total > 100 ? `${Math.round((total - 100) * 100) / 100} en trop` : `${Math.round((100 - total) * 100) / 100} manquant`}, doit faire 100 %)
        </span>
      )}
    </div>
  );
};

/** Co-assureurs : liste variable de porteurs, chacun avec sa quote-part. */
export const CoInsurerFields: FC<{ insurers: ActorOption[] }> = ({ insurers }) => {
  const { values } = useFormikContext<{ coAssureurs: CoInsurerValues[] }>();

  return (
    <FieldArray name="coAssureurs">
      {({ push, remove }) => (
        <div className="flex flex-col gap-3">
          {values.coAssureurs.map((coInsurer, index) => (
            <InsurerRow
              key={index}
              insurers={insurers}
              prefix={`coAssureurs.${index}`}
              personField="coAssureurPersonneMoraleId"
              personId={coInsurer.coAssureurPersonneMoraleId}
              onRemove={() => remove(index)}
            >
              <CheckboxField
                name={`coAssureurs.${index}.aperition`}
                label="Apérition"
                checked={coInsurer.aperition}
              />
            </InsurerRow>
          ))}

          <Button plain onClick={() => push(emptyCoInsurer())}>
            <Plus data-slot="icon" />
            Ajouter un co-assureur
          </Button>
        </div>
      )}
    </FieldArray>
  );
};

/** Ré-assureurs : quote-part et/ou traité en excédent de sinistre (XS). */
export const ReInsurerFields: FC<{ insurers: ActorOption[] }> = ({ insurers }) => {
  const { values } = useFormikContext<{ reAssureurs: ReInsurerValues[] }>();

  return (
    <FieldArray name="reAssureurs">
      {({ push, remove }) => (
        <div className="flex flex-col gap-3">
          {values.reAssureurs.map((reInsurer, index) => (
            <InsurerRow
              key={index}
              insurers={insurers}
              prefix={`reAssureurs.${index}`}
              personField="reAssureurPersonneMoraleId"
              personId={reInsurer.reAssureurPersonneMoraleId}
              onRemove={() => remove(index)}
            >
              <div className="flex flex-wrap items-center gap-4">
                <CheckboxField name={`reAssureurs.${index}.qP`} label="Quote-part" checked={reInsurer.qP} />
                <CheckboxField name={`reAssureurs.${index}.xS`} label="Excédent de sinistre" checked={reInsurer.xS} />
              </div>
              {reInsurer.xS && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <NumberField name={`reAssureurs.${index}.porteeXS`} label="Portée XS" value={reInsurer.porteeXS} />
                  <NumberField
                    name={`reAssureurs.${index}.attachementXS`}
                    label="Attachement XS"
                    value={reInsurer.attachementXS}
                  />
                </div>
              )}
            </InsurerRow>
          ))}

          <Button plain onClick={() => push(emptyReInsurer())}>
            <Plus data-slot="icon" />
            Ajouter un ré-assureur
          </Button>
        </div>
      )}
    </FieldArray>
  );
};

// ---------------------------------------------------------------- primitives

/**
 * Ligne commune aux co-assureurs et ré-assureurs : porteur, compte bancaire, quote-part.
 * Les champs imbriqués sont pilotés directement par setFieldValue, FormikDropdown ne
 * sachant lire que les valeurs de premier niveau.
 */
const InsurerRow: FC<{
  insurers: ActorOption[];
  prefix: string;
  personField: string;
  personId: string;
  onRemove: () => void;
  children?: React.ReactNode;
}> = ({ insurers, prefix, personField, personId, onRemove, children }) => {
  const { setFieldValue, getFieldMeta } = useFormikContext();
  const bankAccounts = useBankAccountOptions(personId);
  const items = toDropdownItems(insurers);
  const accountValue = getFieldMeta<string>(`${prefix}.compteBancaireId`).value;
  const tauxValue = getFieldMeta<number | ''>(`${prefix}.tauxQP`).value;

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
          <Dropdown
            name={`${prefix}.${personField}`}
            label="Compagnie"
            items={items}
            selectedItem={items.find((item) => item.key === personId) ?? null}
            onSelect={(item) => setFieldValue(`${prefix}.${personField}`, item.key)}
            placeholder="Sélectionner une compagnie"
          />
          <Dropdown
            name={`${prefix}.compteBancaireId`}
            label="Compte bancaire"
            items={bankAccounts.options}
            selectedItem={
              bankAccounts.options.find((option) => option.key === accountValue) ?? null
            }
            onSelect={(item) => setFieldValue(`${prefix}.compteBancaireId`, item.key)}
            disabled={!personId}
            placeholder={personId ? 'Sélectionner un compte' : "Sélectionner d'abord une compagnie"}
          />
          <NumberField name={`${prefix}.tauxQP`} label="Quote-part (%)" value={tauxValue} />
        </div>
        <Button plain onClick={onRemove}>
          <Trash2 data-slot="icon" className="text-red-500" />
        </Button>
      </div>
      <div className="mt-3 flex flex-col gap-3">{children}</div>
    </div>
  );
};

const NumberField: FC<{ name: string; label: string; value: number | '' }> = ({
  name,
  label,
  value,
}) => {
  const { setFieldValue } = useFormikContext();
  return (
    <InputText
      name={name}
      label={label}
      type="number"
      value={value}
      onChange={(event) =>
        setFieldValue(name, event.target.value === '' ? '' : Number(event.target.value))
      }
    />
  );
};

const CheckboxField: FC<{ name: string; label: string; checked: boolean }> = ({
  name,
  label,
  checked,
}) => {
  const { setFieldValue } = useFormikContext();
  return (
    <CheckboxComponent
      name={name}
      label={label}
      checked={checked}
      onChange={(next) => setFieldValue(name, next)}
    />
  );
};

const FormikInputNumber: FC<{ name: string; label: string }> = ({ name, label }) => {
  const { getFieldMeta } = useFormikContext();
  return <NumberField name={name} label={label} value={getFieldMeta<number | ''>(name).value} />;
};
